import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useAvatar } from "@/hooks/useAvatar";
import { catalogThumbnailUrl } from "@/lib/avatarThumbnails";
import { preloadGLTF } from "@/lib/avatarPreloader";
import { useAvatarCatalog } from "@/hooks/useAvatarCatalog";
import {
  getPrerenderedByGender,
  type GenderFilter,
  type PrerenderedAvatar,
} from "@/lib/prerenderedAvatars";

const AvatarViewer = lazy(() => import("@/components/AvatarViewer"));

interface PrerenderedAvatarPickerProps {
  defaultGender?: GenderFilter;
  onSelect: (avatar: PrerenderedAvatar) => void;
}

type Filter = "male" | "female" | "all";

function pickInitialFilter(
  defaultGender: GenderFilter | undefined,
  profileSex: GenderFilter,
): Filter {
  const candidate =
    defaultGender === "male" || defaultGender === "female"
      ? defaultGender
      : profileSex === "male" || profileSex === "female"
        ? profileSex
        : null;
  if (candidate === "male" || candidate === "female") return candidate;
  return "all";
}

/**
 * Path 1 — curated GLB picker for the onboarding 3-path screen.
 *
 * Loading strategy (22/05 rewrite — drop ~95 MB of initial download):
 *
 *   - The 6 catalog .glb files in Supabase Storage are 12-19 MB each
 *     (~96 MB total). Rendering all six AvatarViewers at mount was
 *     pulling all of them in parallel, blowing the onboarding budget.
 *
 *   - Now: every card renders the 2D PNG poster by default (~1 MB each,
 *     ~6 MB total — 15x lighter). Only the SELECTED card mounts the
 *     <AvatarViewer> with the heavy .glb. Tapping a different card
 *     switches which one renders 3D.
 *
 *   - Only the selected GLB is fetched. Other cards keep their 2D posters,
 *     preserving bandwidth and decoder memory for the current selection.
 *
 *   - Frozen contract: AvatarViewer / AvatarCore / mirrorAvatarConfig
 *     untouched. Calibration props (scaleOverride / cameraDistanceOverride
 *     / lookAtYOverride / framing / isAvaturnModel) preserved exactly.
 */
export default function PrerenderedAvatarPicker({
  defaultGender,
  onSelect,
}: PrerenderedAvatarPickerProps) {
  const { t } = useLanguage();
  const { sex: profileSex } = useAvatar({ preloadModel: false });
  // Live catalog from `avatar_catalog` — serves the in-code seed array while
  // loading and on any failure, so this screen never depends on the network.
  const { avatars: catalog } = useAvatarCatalog();
  const [filter, setFilter] = useState<Filter>(
    pickInitialFilter(defaultGender, profileSex),
  );
  const [pendingId, setPendingId] = useState<string | null>(null);

  const avatars = useMemo<PrerenderedAvatar[]>(() => {
    if (filter === "all") return catalog;
    return getPrerenderedByGender(filter, catalog);
  }, [filter, catalog]);

  const pending = avatars.find((a) => a.id === pendingId) ?? null;

  // Only the chosen model may consume bandwidth/decoder memory. The previous
  // sequential warm-up still fetched the whole catalog on a metered phone.
  // AvatarViewer shares this exact in-flight request through avatarPreloader.
  useEffect(() => {
    if (!pending) return;
    void preloadGLTF(pending.glbUrl).catch((err) => {
      console.warn('[PrerenderedAvatarPicker] selected preload failed:', err);
    });
  }, [pending]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-[14px] tracking-wide text-white/90">
          {t("prerendered.picker.title")}
        </h3>
        <div className="flex gap-1.5">
          {(["male", "female", "all"] as Filter[]).map((f) => {
            const active = filter === f;
            const label =
              f === "male"
                ? t("prerendered.picker.filter_male")
                : f === "female"
                  ? t("prerendered.picker.filter_female")
                  : t("prerendered.picker.filter_all");
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className="px-3 py-1.5 rounded-lg font-display text-[11px] tracking-wide transition-all active:scale-95"
                style={{
                  background: active
                    ? "rgba(34,211,238,0.15)"
                    : "rgba(255,255,255,0.04)",
                  border:
                    "0.5px solid " +
                    (active
                      ? "rgba(34,211,238,0.40)"
                      : "rgba(255,255,255,0.08)"),
                  color: active ? "rgba(236,254,255,0.95)" : "rgba(255,255,255,0.55)",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {avatars.map((a) => {
          const selected = pendingId === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setPendingId(a.id)}
              className="flex flex-col items-stretch gap-1.5 rounded-2xl p-2 transition-all active:scale-[0.97]"
              style={{
                background:
                  "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)",
                border:
                  "1px solid " +
                  (selected
                    ? "hsl(187 72% 50% / 0.50)"
                    : "rgba(255,255,255,0.05)"),
                boxShadow: selected
                  ? "0 0 16px hsl(187 72% 50% / 0.25)"
                  : "none",
              }}
            >
              <div
                className="relative w-full overflow-hidden rounded-xl"
                style={{
                  height: 140,
                  background:
                    "radial-gradient(circle at center, rgba(34,211,238,0.10) 0%, transparent 65%)",
                }}
              >
                {/* Poster — always rendered. Acts as the skeleton until
                    the 3D viewer mounts on top of it on selection. */}
                <img
                  src={catalogThumbnailUrl(a.glbUrl) ?? a.posterUrl}
                  onError={(event) => {
                    if (event.currentTarget.src !== a.posterUrl) event.currentTarget.src = a.posterUrl;
                  }}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width={140}
                  height={140}
                  className="absolute inset-0 w-full h-full"
                  style={{
                    objectFit: "cover",
                    opacity: selected ? 0 : 1,
                    transition: "opacity 220ms ease-out",
                  }}
                />
                {selected && (
                  <Suspense fallback={null}>
                  <AvatarViewer
                    modelUrl={a.glbUrl}
                    height="100%"
                    enableRotate={false}
                    framing="bust"
                    isAvaturnModel={true}  // LEGACY NAME — Memory L23. Gates AvatarCore calibration profile A. DO NOT pin false.
                    scaleOverride={1.6}
                    cameraDistanceOverride={1.7}
                    lookAtYOverride={2.5}
                  />
                  </Suspense>
                )}
              </div>
              <span className="font-display text-[12px] text-foreground/80 tracking-wide text-center">
                {a.name}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        disabled={!pending}
        onClick={() => pending && onSelect(pending)}
        className="w-full h-12 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.98] disabled:opacity-40"
        style={{
          background: pending
            ? "linear-gradient(135deg, rgba(34,211,238,0.40), rgba(34,211,238,0.25))"
            : "rgba(255,255,255,0.04)",
          border: "0.5px solid rgba(34,211,238,0.55)",
          color: "rgba(236,254,255,0.98)",
        }}
      >
        {t("prerendered.picker.confirm")}
      </button>
    </div>
  );
}
